import { runInAction } from 'mobx';
import {
  popupStore,
  userInfoStore,
  gameResultStore,
  gameConfigStore,
  profileAvatarStore,
  parseQuery,
  RoundStatus,
  applicationStore,
  DefaultQueryParams,
  GameModes,
  notificationStore,
  betStore,
  BetStatus,
  BetResponseCode,
  FreeBetWinningType,
  iframeService,
  loadingStore,
  freeBetStore,
} from '@pascal/pascal-core';

import { gameStore, roundTimeStore, doBetStore, gameRoundsInfoStore, oddStore } from '../store';

import { worker } from '../index';
import { liveConnection } from '../core';
import { betDataStore } from '../store';
import { gameLiveConnection } from './GameConnection';
import { gameResultsDataStore } from '../store';
import { animationStore } from '../store/animationStore';
import { defaultMinOdd, minOddConstant } from '../enums/gameConstants';
import { soundService, soundTypes } from './soundService';

let timeOutId;

class GameService {
  isReceivePong = false;

  onGetInitialStateResult({
    rate,
    limits,
    gameTypeInfos,
    balance,
    avatarId,
    precision,
    currencyCode,
    partnerWageringOdd,
    userFreebets,
  }) {
    const { Started, OnFinish, Finished, OnHold } = RoundStatus;
    const { isDemo } = applicationStore;
    const { [DefaultQueryParams.Mode]: mode } = parseQuery(window.location.search);
    // soundService.playMusic();
    gameConfigStore.setLimits(limits);

    if (mode === GameModes.Fun) {
      applicationStore.setIsDemo(true);
    }

    if (partnerWageringOdd) {
      gameStore.setPartnerWageringOdd(partnerWageringOdd);
      if (!isDemo) {
        gameStore.setMinOdd(partnerWageringOdd);
        if (partnerWageringOdd > defaultMinOdd) {
          gameStore.setDefaultMinOdd(partnerWageringOdd);
        }
      } else {
        gameStore.setMinOdd(minOddConstant);
        gameStore.setDefaultMinOdd(defaultMinOdd);
      }
    }
    const { defaultBetAmount, minBet } = limits;

    const roundsInfoData = {};
    const resultsData = {};
    const betsCloseInitial = {};
    const allGameTypes = [];
    const initialOddData = {};
    const betsSectionsInitialData = {};
    const initialCurrentBetAmount = {};

    gameTypeInfos.forEach(({ gameTypeId, roundInfo, results, currentBets }) => {
      const { status, second, roundId } = roundInfo;
      const gameDataByRound = results.find((result) => result.roundId === roundId);

      resultsData[gameTypeId] = results;
      betsCloseInitial[gameTypeId] = second <= 0;
      allGameTypes.push(gameTypeId);
      initialOddData[gameTypeId] = gameDataByRound.roundData;

      betsSectionsInitialData[gameTypeId] = {
        isAutoBet: false,
        selectedAutoBetCount: null,
        isAutoCashOut: false,
        autoCashOutOdd: partnerWageringOdd && !isDemo ? partnerWageringOdd : defaultMinOdd,
        betAmount: defaultBetAmount || minBet,
        freeBetId: null,
        readyForAutoBet: null,
        freeBetAmount: null,
        freeBetCurrentOdd: null,
      };

      if (currentBets && currentBets.length) {
        const bets = currentBets.map((bet) => {
          return {
            ...bet,
            gameTypeId,
          };
        });

        if (!isDemo) {
          const userCurrentBet = currentBets.find(
            (item) => item?.playerExternalId === userInfoStore.playerExternalId
          );
          if (userCurrentBet && userCurrentBet.status === BetStatus.Pending) {
            const { freeBetId } = userCurrentBet;
            const currentUserFreebet = userFreebets?.find((item) => item.id === freeBetId);
            if (freeBetId) {
              oddStore.setCurrentFreeBetMaxPayout(gameTypeId, null, currentUserFreebet);
              if (
                (currentUserFreebet.winType || freeBetStore.activeFreeBet.winType) ===
                FreeBetWinningType.Win
              ) {
                doBetStore.setFreeBetType(FreeBetWinningType.Win, gameTypeId);
              }
            }
            initialCurrentBetAmount[gameTypeId] = userCurrentBet?.amount;
            doBetStore.setInitialDefaultDoBetData(
              gameTypeId,
              userCurrentBet,
              currentUserFreebet?.minOdd
            );
            doBetStore.setCashOutDisabled(true, gameTypeId);

            if (partnerWageringOdd) {
              doBetStore.setIsDisabled(true, gameTypeId);
            }

            if (userCurrentBet.freeBetId) {
              const currentUserFreebet = userFreebets?.find((item) => item.id === freeBetId);
              doBetStore.setFreeBeetDefaultOdd(userCurrentBet.freeBetId, currentUserFreebet);
            }
          } else {
            doBetStore.setInitialDefaultDoBetData(gameTypeId);
          }
        } else {
          doBetStore.setInitialDefaultDoBetData(gameTypeId);
        }
        betStore.updateLiveBets(bets);
      } else {
        initialCurrentBetAmount[gameTypeId] = null;
        doBetStore.setInitialDefaultDoBetData(gameTypeId);
      }

      if (status === OnHold) {
        gameStore.setIsBetsClosed(true);
      }
      if (status === Started) {
        const isBetClosed = second <= 0;
        doBetStore.setCashOutDisabled(isBetClosed, gameTypeId);
        betsSectionsInitialData[gameTypeId].readyForAutoBet = !isBetClosed;
      }
      if (status === OnFinish || status === Finished) {
        gameStore.setIsBetsClosed(false);
      }

      roundsInfoData[gameTypeId] = roundInfo;

      gameStore.setRoundId(roundInfo.roundId, gameTypeId);

      const finishBlueLightningAnim =
        status === RoundStatus.OnFinish || (status === RoundStatus.Finished && second <= 0);
      const roundAnimState =
        status === RoundStatus.OnHold
          ? true
          : status === RoundStatus.OnFinish || status === RoundStatus.Finished
          ? false
          : second <= 0.5;
      animationStore.setInitialAnimationState(roundAnimState, finishBlueLightningAnim, gameTypeId);
      this.sendInitialWorker(roundInfo, gameTypeId);
    });

    oddStore.setInitialCurrentBetAmount(initialCurrentBetAmount);
    oddStore.setInitialOddData(initialOddData);
    betDataStore.setInitialBetSectionsData(betsSectionsInitialData);
    gameRoundsInfoStore.setInitialRoundsInfo(roundsInfoData);
    gameResultsDataStore.setInitialGameResults(gameTypeInfos[0].gameTypeId, resultsData);
    gameResultStore.setGameResults(resultsData[allGameTypes[0]]);
    gameStore.setAllGameTypes(allGameTypes);
    gameStore.setInitialBetsClosed(betsCloseInitial);
    gameStore.setActiveGameType(allGameTypes[0]);

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);
    profileAvatarStore.updateAvatarId(avatarId);
    profileAvatarStore.updateInitialAvatarId(avatarId);

    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });

    gameStore.setActiveGameType(gameTypeInfos[0].gameTypeId);
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const { gameTypeId, userId: betUserId, freeBetId } = data;

    betStore.updateLiveBets(data);
    if (+userId === +betUserId) {
      const { isAutoBet } = betDataStore.betsSectionsData[gameTypeId];

      if (isAutoBet) {
        betDataStore.decreaseAutoBetCount(gameTypeId);
      }

      if (freeBetId) {
        freeBetStore.handleFreeBetDoBetResult(freeBetId);
        doBetStore.setFreeBeetDefaultOdd(freeBetId);
        doBetStore.setCurrentFreeBetId(freeBetId, gameTypeId);
        oddStore.setCurrentFreeBetMaxPayout(gameTypeId);

        if (freeBetStore.activeFreeBet.winType === FreeBetWinningType.Win) {
          doBetStore.setFreeBetType(FreeBetWinningType.Win, gameTypeId);
        }

        if (data.betData) {
          doBetStore.setFreeBetCurrentOdd(data.betData, gameTypeId);
        }

        doBetStore.setCurrentFreeBetAmount(freeBetId, gameTypeId);

        if (
          doBetStore.doBetSectionsData[gameTypeId].freeBetId &&
          freeBetStore.activeFreeBet.minOdd > 1.01
        ) {
          doBetStore.setIsDisabled(true, gameTypeId);
        }
      }

      doBetStore.setIsBetPendingState(false, gameTypeId);

      if (gameStore.partnerWageringOdd) {
        doBetStore.setIsDisabled(true, gameTypeId);
      }

      oddStore.setCurrentBetAmount(data.amount, gameTypeId);
      oddStore.clearCurrentWinAmount(gameTypeId);
      doBetStore.setDoBetResult(data);
    }
  }

  handleDoBetResultValidation({ responseCode, gameTypeId }) {
    doBetStore.setIsBetPendingState(false, gameTypeId);
    const { InsufficientFunds, MoneyTransactionError, GeneralError, UnAuthorizedUser } =
      BetResponseCode;
    if (
      [InsufficientFunds, MoneyTransactionError, GeneralError, UnAuthorizedUser].includes(
        responseCode
      )
    ) {
      betDataStore.setIsAutoBet(false, gameTypeId);
    }
    // todo this comment for reBet function;
    // const roundId = gameStore.roundId[gameTypeId];
    // const { isAutoBet, isAutoCashOut, autoCashOutOdd, betAmount, readyForAutoBet } = betDataStore.betsSectionsData[gameTypeId];
    // if (isAutoBet && readyForAutoBet) {
    //   const autoBetModel = {
    //     gameTypeId,
    //     amount: +betAmount,
    //     betData: isAutoCashOut && autoCashOutOdd,
    //     roundId,
    //   };
    //   gameLiveConnection.doBet(autoBetModel);
    // }
    // doBetStore.resetDoBetStoreBySection(index);
    // doBetStore.changeBetIsDisabled(index, false);
    notificationStore.setNotificationType({ responseCode });
  }

  handleResultReport({ data: { gameTypeId, resultReportData } }) {
    const { isDemo } = applicationStore;
    const { bets, odd } = resultReportData;
    const { partnerWageringOdd } = gameStore;
    const { setOddData } = oddStore;
    const isDisabled = !!doBetStore.doBetSectionsData[gameTypeId]?.isDisabled;
    const cashOutDisabled = !!doBetStore.cashOutDisabled[gameTypeId];

    setOddData(odd.toFixed(2), gameTypeId);

    if (cashOutDisabled) {
      doBetStore.setCashOutDisabled(false, gameTypeId);
    }

    if (!gameStore.isBetsClosedByGameType?.[gameTypeId]) {
      gameStore.setIsBetsClosed(true, gameTypeId);
    }

    if (partnerWageringOdd && !isDemo && isDisabled && odd >= partnerWageringOdd) {
      doBetStore.setIsDisabled(false, gameTypeId);
    }

    if (doBetStore.doBetSectionsData?.[gameTypeId]?.freeBetId) {
      if (isDisabled && odd >= doBetStore.freeBetMinOdd) {
        doBetStore.setIsDisabled(false, gameTypeId);
      }
    }

    if (bets?.length) {
      const currentBets = betStore.liveBets;
      const winData = bets.find((item) => +userInfoStore.userId === +item.userId);
      if (winData?.freeBetId) {
        freeBetStore.freeBetTotalWinCalculator(winData);
      }
      oddStore.setCurrentBetAmount(null, gameTypeId);
      doBetStore.setBetAccepted(winData.id, false, gameTypeId);
      doBetStore.handleWinData({ ...winData, gameTypeId });
      oddStore.clearCurrentWinAmount(gameTypeId);

      const sortWiningPendingLivBets = currentBets.map((bet) => {
        const res = bets.find((item) => item.id === bet.id);
        return res ? { ...res, gameTypeId } : bet;
      });
      betStore.setLiveBets(sortWiningPendingLivBets);
    }
  }

  handleRoundStart({
    data: {
      startedRound: { roundId, gameTypeId, duration },
      results,
      underMaintenance,
    },
  }) {
    if (underMaintenance) {
      gameStore.setUnderMaintenance(gameTypeId);
      animationStore.setToggleGameClosed(true, gameTypeId);
      if (gameStore.allGameTypes.length === gameStore.underMaintenance.length) {
        iframeService.sendReload();
      }
    }
    gameLiveConnection.getBalance();
    betDataStore.setReadyForAutoBet(true, gameTypeId);
    animationStore.setFinishAnim(false, gameTypeId);

    if (animationStore.isRotateByGameType) {
      animationStore.setIsRotateByGameType(false, gameTypeId);
    }
    gameResultsDataStore.updateResultsByGameType(gameTypeId, results);
    this.sendWorker(duration, gameTypeId);
    gameStore.setRoundId(roundId, gameTypeId);
    gameStore.setIsBetsClosed(false, gameTypeId);
    const currentLiveBets = betStore.liveBets.filter((item) => item.gameTypeId !== gameTypeId);
    betStore.setLiveBets(currentLiveBets);
    const { isAutoBet, isAutoCashOut, autoCashOutOdd, betAmount, selectedAutoBetCount } =
      betDataStore.betsSectionsData[gameTypeId];
    if (isAutoBet && selectedAutoBetCount) {
      // if (applicationStore.isDemo) {
      //   doBetStore.doBetSender(gameTypeId);
      // } else {
      const autoBetModel = {
        gameTypeId,
        amount: +betAmount,
        betData: isAutoCashOut && autoCashOutOdd,
        freeBetId: freeBetStore.activeFreeBet.id || null,
        roundId,
      };
      gameLiveConnection.doBet(autoBetModel);
      // }
    }
  }

  handleRoundFin({ data: { bets, finishedRound } }) {
    const { gameTypeId } = finishedRound;
    gameResultsDataStore.updateFinishedRoundStatus(finishedRound);
    animationStore.setIsRotateByGameType(false, gameTypeId);
    animationStore.setLightningAnimationStart(false, gameTypeId);
    doBetStore.handleClearRoundFin(bets, gameTypeId);
    oddStore.setCurrentBetAmount(null, gameTypeId);
    animationStore.setFinishAnim(true, gameTypeId);
    const { activeGameType } = gameStore;
    const { isGameClosedByGameType } = animationStore;
    if (gameTypeId === activeGameType && !isGameClosedByGameType[gameTypeId]) {
      soundService.play(soundTypes.finishLightning);
    }

    const { liveBets = [] } = betStore;
    if (oddStore.currentWinAmount[gameTypeId]) {
      if (bets.freeBetId) {
        oddStore.setCurrentFreeBetMaxPayout(gameTypeId, null);
      }
      oddStore.clearCurrentWinAmount(gameTypeId);
      // oddStore.setIsMaxWinByGameType(false, gameTypeId);
    }

    // if (applicationStore.isDemo) {
    // const finishWinLiveBets = liveBets.map((item) => {
    //   if (item.status !== BetStatus.Win && !item.winAmount && item.gameTypeId === gameTypeId) {
    //     return { ...item, status: BetStatus.Lose, odd: 0, winAmount: 0, isFinished: true };
    //   }
    //   return item;
    // });
    // if (liveBets.find((item) => item.roundId === finishedRound.id)) {
    //   const demoBetsData = liveBets.find(
    //     (betData) => !betData?.playerExternalId && !(betData.status === BetStatus.Win)
    //   );
    //   betStore.updateDemoBetHistory([demoBetsData]);
    // }
    //
    // betStore.setLiveBets(finishWinLiveBets);
    // } else {
    const finishLiveBets = liveBets.map((bet) => {
      if (bet.gameTypeId === gameTypeId && bet.status !== BetStatus.Win) {
        return {
          ...bet,
          isFinished: true,
          odd: 0,
          winAmount: 0,
          winCoinAmount: 0,
          status: BetStatus.Lose,
        };
      }
      return bet;
    });

    const userFinishBet = bets.find((bet) => +bet.userId === +userInfoStore.userId);

    if (
      gameStore.partnerWageringOdd ||
      (userFinishBet?.freeBetId && doBetStore.doBetSectionsData[gameTypeId].isDisabled)
    ) {
      doBetStore.setIsDisabled(false, gameTypeId);
    }

    betStore.setLiveBets(finishLiveBets);
    // }
  }

  handleBetsClosed({ data: { gameTypeId } }) {
    const { activeGameType } = gameStore;
    const { isGameClosedByGameType } = animationStore;
    if (gameTypeId === activeGameType && !isGameClosedByGameType[gameTypeId]) {
      soundService.play(soundTypes.startLightning);
    }
    animationStore.setLightningAnimationStart(true, gameTypeId);
    animationStore.setIsRotateByGameType(true, gameTypeId);
    doBetStore.setCashOutDisabled(true, gameTypeId);
    betDataStore.setReadyForAutoBet(false, gameTypeId);
    gameStore.setIsBetsClosed(true, gameTypeId);
  }

  handleCashOutResult(cashOutData) {
    const bets = betStore.liveBets;
    let cashOutLivBets = [];
    if (cashOutData?.userId === +userInfoStore.userId) {
      const { gameTypeId, betId } = cashOutData;
      doBetStore.handleWinData(cashOutData);
      doBetStore.setBetAccepted(betId, false, gameTypeId);
      cashOutLivBets = bets.map((bet) => {
        if (bet.id === cashOutData.id) {
          if (cashOutData.freeBetId) {
            freeBetStore.freeBetTotalWinCalculator(cashOutData);
          }
          return cashOutData;
        }
        return bet;
      });
      oddStore.setCurrentBetAmount(null, gameTypeId);

      setTimeout(() => {
        runInAction(() => oddStore.clearCurrentWinAmount(gameTypeId));
      }, 2000);

      betStore.setLiveBets(cashOutLivBets);
      // if (oddStore.isMaxWinByGameType) {
      //   oddStore.setIsMaxWinByGameType(false, gameTypeId);
      // }
    } else {
      const otherCashOutBets = bets.map((bet) => {
        if (bet.id === cashOutData.id) {
          return cashOutData;
        }
        return bet;
      });
      betStore.setLiveBets(otherCashOutBets);
    }
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false).then(() => popupStore.toggleReconnect(false));
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendInitialWorker(roundInfo, gameTypeId) {
    if (roundInfo) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage({
        gameTypeId: gameTypeId,
        second: roundInfo?.second > 0 ? Math.floor(roundInfo?.second) : null,
        duration: roundInfo?.duration,
      });
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  countDown({ data }) {
    roundTimeStore.setTime(data.second, data.gameTypeId);
  }

  sendWorker(second, gameTypeId) {
    if (second) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage({ second, gameTypeId });
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
